import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { productService } from '../services'
import {
  formatVatPercentDisplay,
  formatVatPercentForForm,
  parseVatPercentInput,
} from '../utils/productManagementUtils'
import { enrichVatReviewRow } from '../utils/shipmentVatReview'

function formatMoney(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—'
  }
  return `৳${Number(value).toFixed(2)}`
}

function formatDialogError(value) {
  if (value == null || value === '') return null
  return Array.isArray(value) ? value.join(', ') : String(value)
}

/**
 * Review product VAT before SO / shipment create (integration-enabled shops only).
 * Editing VAT updates the catalog product globally via the existing product API.
 */
export default function ShipmentVatReviewDialog({
  open,
  onClose,
  rows = [],
  submitting = false,
  /** Upstream create/dispatch failure — shown on this last-step dialog. */
  submitError = null,
  onConfirm,
  onVatUpdated,
}) {
  const [localRows, setLocalRows] = useState([])
  const [loadingVat, setLoadingVat] = useState(false)
  const [error, setError] = useState(null)
  const [editingProductId, setEditingProductId] = useState(null)
  const [vatDraft, setVatDraft] = useState('')
  const [savingProductId, setSavingProductId] = useState(null)
  const submitErrorRef = useRef(null)
  const formattedSubmitError = formatDialogError(submitError)

  useEffect(() => {
    if (!open) {
      setLocalRows([])
      setError(null)
      setEditingProductId(null)
      setVatDraft('')
      setSavingProductId(null)
      setLoadingVat(false)
      return
    }

    let cancelled = false
    const seed = (rows || []).map((r) => ({ ...r }))
    setLocalRows(seed)
    setError(null)

    const productIds = [
      ...new Set(
        seed
          .map((r) => String(r.productId || '').trim())
          .filter(Boolean),
      ),
    ]

    if (productIds.length === 0) return undefined

    ;(async () => {
      setLoadingVat(true)
      try {
        const results = await Promise.all(
          productIds.map(async (id) => {
            try {
              const product = await productService.getProductById(id)
              return { id, product, ok: true }
            } catch (err) {
              return { id, err, ok: false }
            }
          }),
        )
        if (cancelled) return
        const byId = new Map()
        for (const result of results) {
          if (!result.ok || !result.product) continue
          const vatRaw = result.product.vat
          const vat =
            vatRaw === null || vatRaw === undefined || vatRaw === ''
              ? null
              : Number(vatRaw)
          byId.set(result.id, {
            vat: Number.isFinite(vat) ? vat : null,
            source: result.product.source || 'shwapno',
          })
        }
        setLocalRows((prev) =>
          prev.map((row) => {
            const id = String(row.productId || '').trim()
            const latest = byId.get(id)
            if (!latest) return row
            return {
              ...row,
              vat: latest.vat,
              source: latest.source || row.source,
            }
          }),
        )
      } finally {
        if (!cancelled) setLoadingVat(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, rows])

  useEffect(() => {
    if (!open || !formattedSubmitError) return undefined
    const frame = requestAnimationFrame(() => {
      submitErrorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    })
    return () => cancelAnimationFrame(frame)
  }, [open, formattedSubmitError])

  const enriched = useMemo(
    () => localRows.map((row) => enrichVatReviewRow(row)),
    [localRows],
  )

  const missingRows = useMemo(
    () => enriched.filter((r) => r.vatMissing),
    [enriched],
  )

  const canContinue =
    !submitting &&
    !loadingVat &&
    !savingProductId &&
    !editingProductId &&
    missingRows.length === 0 &&
    enriched.length > 0

  const startEdit = useCallback((row) => {
    setError(null)
    setEditingProductId(row.productId)
    setVatDraft(formatVatPercentForForm(row.vat))
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingProductId(null)
    setVatDraft('')
  }, [])

  const saveVat = useCallback(async () => {
    const productId = editingProductId
    if (!productId) return
    const parsed = parseVatPercentInput(vatDraft)
    if (parsed.kind === 'invalid') {
      setError(parsed.reason)
      return
    }
    if (parsed.kind === 'absent') {
      setError('VAT% is required')
      return
    }

    setSavingProductId(productId)
    setError(null)
    try {
      await productService.updateProduct(productId, { vat: parsed.value })
      setLocalRows((prev) =>
        prev.map((row) =>
          String(row.productId) === String(productId)
            ? { ...row, vat: parsed.value }
            : row,
        ),
      )
      onVatUpdated?.(productId, parsed.value)
      setEditingProductId(null)
      setVatDraft('')
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not update product VAT%',
      )
    } finally {
      setSavingProductId(null)
    }
  }, [editingProductId, vatDraft, onVatUpdated])

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Review product VAT details</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Confirm VAT before creating the sales order / shipment. Unit price is
          the VAT-inclusive selling price; excl. VAT uses the existing SAP
          COND_VALUE formula.
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          Editing VAT% here updates the product in the catalog for all future
          orders and shipments, not only this shipment. The same product update
          rules and permissions apply as in Product Management.
        </Alert>

        {formattedSubmitError ? (
          <Box ref={submitErrorRef} sx={{ mb: 2 }}>
            <Alert severity="error" role="alert">
              {formattedSubmitError}
            </Alert>
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formatDialogError(error)}
          </Alert>
        ) : null}

        {missingRows.length > 0 ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            VAT% is required for Shwapno products before continuing:{' '}
            {missingRows
              .map((r) => r.productName + (r.articleCode ? ` (${r.articleCode})` : ''))
              .join(', ')}
            . Add or edit VAT% below.
          </Alert>
        ) : null}

        {loadingVat ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Article Number</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">VAT %</TableCell>
                  <TableCell align="right">Unit Price + VAT</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enriched.map((row) => {
                  const isEditing =
                    editingProductId &&
                    String(editingProductId) === String(row.productId)
                  const saving =
                    savingProductId &&
                    String(savingProductId) === String(row.productId)
                  return (
                    <TableRow
                      key={row.key}
                      sx={
                        row.vatMissing
                          ? { backgroundColor: 'error.lighter' }
                          : undefined
                      }
                    >
                      <TableCell>{row.articleCode || '—'}</TableCell>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell align="right">
                        {row.requiresVat && row.vatMissing
                          ? '—'
                          : formatMoney(row.unitPriceExcl)}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={vatDraft}
                            onChange={(e) => setVatDraft(e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            disabled={!!saving}
                            sx={{ width: 96 }}
                          />
                        ) : row.requiresVat ? (
                          formatVatPercentDisplay(row.vat)
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(row.unitPriceInclDisplay)}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(row.totalIncl)}
                      </TableCell>
                      <TableCell align="right">
                        {!row.productId ? null : isEditing ? (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={saveVat}
                              disabled={!!saving}
                              aria-label="Save VAT"
                            >
                              {saving ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CheckIcon fontSize="small" />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={cancelEdit}
                              disabled={!!saving}
                              aria-label="Cancel VAT edit"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => startEdit(row)}
                            disabled={
                              submitting ||
                              !!savingProductId ||
                              !!editingProductId
                            }
                            aria-label="Edit VAT"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
                {enriched.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        No products selected for this shipment.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Back
        </Button>
        <Button
          variant="contained"
          disabled={!canContinue}
          onClick={() => onConfirm?.(localRows)}
        >
          {submitting ? 'Creating…' : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
